import AlertComparaCarCrm from '@comparacar/components/src/generic/AlertComparaCarCrm'
import AlertComparaCarSecurity from '@comparacar/components/src/generic/AlertComparaCarSecurity'
import { iBannerProps } from '@comparacar/components/src/generic/Banner'
import BannerCarrousel from '@comparacar/components/src/generic/BannerCarrousel'
import ProviderBanner from '@comparacar/components/src/generic/ProviderBanner'
import SearchInput from '@comparacar/components/src/generic/SearchInput'
import LoadingSources from '@comparacar/components/src/result/LoadingSources'
import Pagination from '@comparacar/components/src/result/Pagination'
import SortMenu from '@comparacar/components/src/result/SortMenu'
import { getProvider } from '@comparacar/components/src/state/brands'
import engineState from '@comparacar/components/src/state/engine'
import isFirstLoadingState from '@comparacar/components/src/state/isFirstLoading'
import isPageTransitioningState from '@comparacar/components/src/state/isPageTransitioning'
import { translate } from '@comparacar/lib/src/translations'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import React from 'react'
import { useRecoilValue } from 'recoil'

const EmptyState = dynamic(() => import('@comparacar/components/src/atoms/EmptyState'))
const FiltersCar = dynamic(() => import('@comparacar/components/src/assets/FiltersCar'))
const Card = dynamic(() => import('@comparacar/components/src/generic/CardDesktop'))
const StartsAtCard = dynamic(() => import('@comparacar/components/src/result/StartsAtCardDesktop'))
const Filters = dynamic(() => import('@comparacar/components/src/result/Filters'))

interface iResultDesktop {
  data: ResultCard[]
  paginationInfo: PaginationResult
  banners: iBannerProps[]
  provider?: string
}
const ResultDesktop: React.FC<iResultDesktop> = ({ data, paginationInfo, banners }) => {
  const router = useRouter()
  const { source } = router.query
  const providerData = useRecoilValue(getProvider(source as string))
  const isPageTransitioning = useRecoilValue(isPageTransitioningState)
  const engine = useRecoilValue(engineState)
  const isFirstLoading = useRecoilValue(isFirstLoadingState)

  const handleSelect = (option?: AutocompleteType | null) => {
    const newQuery = option ? option.value.split('/') : []
    router.push({
      pathname: router.pathname,
      query: {
        ...router.query,
        slug: newQuery,
        sort: router.query.sort || undefined
      }
    })
  }

  return (
    <>
      <BannerCarrousel WrapperProps={{ position: 'relative', width: 'inherit', height: 250 }} banners={banners} />
      <Box sx={{ mt: 3 }}>
        <Grid container spacing={3}>
          <Grid size={12}>
            <Box component={Paper} sx={{ p: 3 }}>
              <Stack direction="column" spacing={2}>
                <SearchInput fullWidth onSelect={option => handleSelect(option)} />
              </Stack>
            </Box>
          </Grid>
          {isFirstLoading ? (
            <Grid size={12}>
              <LoadingSources />
            </Grid>
          ) : !data.length ? (
            <>
              <Grid size="auto">
                <Paper sx={{ minWidth: '328px', width: '100%', minHeight: '50vh', position: 'relative' }}>
                  <Filters />
                </Paper>
              </Grid>
              <Grid size="grow">
                <Grid container>
                  <Box
                    sx={{
                      flex: 1,
                      padding: '24px 0 0 24px '
                    }}
                  >
                    {providerData?.detail && Object.keys(providerData?.detail).length !== 0 && (
                      <Grid size={12} sx={{ mb: 2 }}>
                        <ProviderBanner provider={providerData} />
                      </Grid>
                    )}
                    <EmptyState
                      title={translate('empty.emptyResult')}
                      subtitle={
                        <>
                          <Typography textAlign="center" variant="body1" color="text.secondary">
                            {translate('empty.message1')}
                          </Typography>
                          {engine === 'subscribe' && (
                            <>
                              <Typography textAlign="center" variant="body1" color="text.secondary">
                                {translate('empty.message2', { strong: translate('empty.message2Strong') })}
                              </Typography>
                            </>
                          )}
                        </>
                      }
                      Image={<FiltersCar />}
                    />
                  </Box>
                </Grid>
              </Grid>
            </>
          ) : (
            <>
              <Grid size="auto">
                <Paper>
                  <Filters />
                </Paper>
              </Grid>
              <Grid size="grow" component="section" id="offers">
                <Grid container>
                  {providerData?.detail && Object.keys(providerData?.detail).length !== 0 && (
                    <Grid size={12} mb={3}>
                      <ProviderBanner provider={providerData} />
                    </Grid>
                  )}

                  <Grid size={12}>
                    <Stack spacing={3}>
                      <Stack justifyContent="space-between" alignItems="center" direction="row">
                        <SortMenu />
                        <Typography color="text.secondary" variant="caption">
                          {paginationInfo.resultsCount} oferta(s)
                        </Typography>
                      </Stack>
                      <AlertComparaCarCrm />
                      <Stack spacing={3}>
                        {data.map(item => {
                          if (item.type === 'STARTAT') {
                            return <StartsAtCard data={item} key={item.id} />
                          }
                          return <Card data={item} key={item.id} />
                        })}
                      </Stack>
                      <AlertComparaCarSecurity />
                      <Pagination
                        sx={{
                          '& .MuiPagination-ul': {
                            justifyContent: 'center'
                          }
                        }}
                        shape="rounded"
                        size="large"
                        count={paginationInfo.pagesCount}
                        siblingCount={2}
                        boundaryCount={0}
                        showFirstButton
                        showLastButton
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </>
  )
}

export default ResultDesktop
